import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";
import { PRODUCT_REFERENCE, ProductReference } from "@/data/productReference";
import { useI18n } from "@/i18n";

// Import product feature images
import y2SpecsImage from "@/assets/product-features/y2-specifications.jpg";
import f2SpecsImage from "@/assets/product-features/f2-specifications.jpg";
import boostingPumpImage from "@/assets/product-features/boosting-pump-system.jpg";
import sensitiveInductionImage from "@/assets/product-features/sensitive-induction.jpg";
import childrenSeatImage from "@/assets/product-features/children-toilet-seat.jpg";
import warmAirImage from "@/assets/product-features/warm-air-drying.jpg";
import seatHeatingImage from "@/assets/product-features/seat-heating-system.jpg";
import healthyLifeImage from "@/assets/product-features/healthy-life-collection.jpg";
import largeComfortImage from "@/assets/product-features/large-seat-comfort.png";
import m6SpecsImage from "@/assets/product-features/m6-specifications.png";
import m9SpecsImage from "@/assets/product-features/m9-specifications.png";
import i6SpecsImage from "@/assets/product-features/i6-specifications.png";
import m2SpecsImage from "@/assets/product-features/m2-specifications.png";
import tb602SpecsImage from "@/assets/product-features/tb602-specifications.png";
import u6SpecsImage from "@/assets/product-features/u6-specifications.png";
import h60SpecsImage from "@/assets/product-features/h60-specifications.png";
import te30SpecsImage from "@/assets/product-features/te30-specifications.png";
import q6SpecsImage from "@/assets/product-features/q6-specifications.png";
import q9SpecsImage from "@/assets/product-features/q9-specifications.png";
import tq07SpecsImage from "@/assets/product-features/tq07-specifications.png";

interface ProductCatalogProps {
  selectedCategory?: string;
}

interface ProductFeature {
  id: string;
  title: string;
  description: string;
  image: string;
  category: string[];
}

const productFeatures: ProductFeature[] = [
  {
    id: "boosting-pump",
    title: "Boosting Pump & Sinking Mode Cistern",
    description: "Lower noise flushing system with built-in water tank design for efficient water usage",
    image: boostingPumpImage,
    category: ["tech-forward", "wellness-focused"]
  },
  {
    id: "sensitive-induction",
    title: "Sensitive Induction & Human Perception",
    description: "Smart sensing technology that automatically opens when approaching and closes when leaving",
    image: sensitiveInductionImage,
    category: ["tech-forward", "flagship"]
  },
  {
    id: "children-seat",
    title: "Comfortable Children's Toilet Seat & User Memory",
    description: "Adaptable design for family use with personalized memory settings for different users",
    image: childrenSeatImage,
    category: ["wellness-focused", "luxury"]
  },
  {
    id: "warm-air-drying",
    title: "Warm Air Drying & Nozzle Self-Cleaning",
    description: "Advanced hygiene features with automatic nozzle cleaning and gentle warm air drying",
    image: warmAirImage,
    category: ["wellness-focused", "luxury"]
  },
  {
    id: "seat-heating",
    title: "Seat Heating & Double Waterway Design",
    description: "Thermostatic heated seat with dual independent waterways for optimal comfort and hygiene",
    image: seatHeatingImage,
    category: ["luxury", "wellness-focused"]
  },
  {
    id: "healthy-life",
    title: "Healthy Life Collection",
    description: "Complete range of smart toilet solutions for modern healthy living",
    image: healthyLifeImage,
    category: ["flagship", "wellness-focused"]
  },
  {
    id: "large-comfort",
    title: "Large Seat - Your Comfort Zone",
    description: "95mm wider seating area with slightly curved design for enhanced comfort and larger seating experience",
    image: largeComfortImage,
    category: ["luxury", "wellness-focused"]
  },
  {
    id: "m9-specs",
    title: "M9 Model - Premium Smart Features",  
    description: "Instant heating, foam shield protection, UV sterilization, and user memory functions with children washing mode",
    image: m9SpecsImage,
    category: ["flagship", "wellness-focused"]
  },
  {
    id: "i6-specs",
    title: "i6 Model - Magic Deodorization System",
    description: "Advanced deodorization technology with foam shield, UV sterilization, and automatic sensor controls",
    image: i6SpecsImage,
    category: ["tech-forward", "wellness-focused"]
  },
  {
    id: "m2-specs",
    title: "M2 Model - User-Friendly Design",
    description: "360° foam shield, hidden foam bottle, night ambient lighting with comprehensive hygiene features",
    image: m2SpecsImage,
    category: ["wellness-focused", "luxury"]
  },
  {
    id: "tb602-specs",
    title: "TB602 Model - LED Display Technology",
    description: "Modern LED display panel with foot sensor flushing, remote control, and UV sterilization features",
    image: tb602SpecsImage,
    category: ["tech-forward", "luxury"]
  },
  {
    id: "u6-specs",
    title: "U6 Model - High-End Features Collection",
    description: "Premium model with foam shield, magic deodorization, UV sterilization, and men's flush recognition",
    image: u6SpecsImage,
    category: ["flagship", "luxury"]
  },
  {
    id: "h60-specs",
    title: "H60 Model - Hidden Water Tank Design",
    description: "Wall-mounted design with hidden water tank, pelvic UV sterilization, and space-saving installation",
    image: h60SpecsImage,
    category: ["tech-forward", "wellness-focused"]
  },
  {
    id: "te30-specs",
    title: "TE30 Model - Compact Smart Solution",
    description: "Space-efficient design with foot sensor flushing, pelvic UV sterilization, and wireless remote control",
    image: te30SpecsImage,
    category: ["wellness-focused", "tech-forward"]  
  },
  {
    id: "q6-specs",
    title: "Q6 Model - Water Spray Massage System",
    description: "Advanced massage features with ambient lighting, user memory modes, and comprehensive hygiene functions",
    image: q6SpecsImage,
    category: ["flagship", "wellness-focused"]
  },
  {
    id: "q9-specs", 
    title: "Q9 Model - Infrared Temperature Control",
    description: "Premium model with infrared temperature lighting, built-in aromatherapy, and advanced sensor technology",
    image: q9SpecsImage,
    category: ["luxury", "flagship"]
  },
  {
    id: "tq07-specs",
    title: "TQ07 Series - Kick & Flush Technology",
    description: "Innovative foot sensor flushing system with foam shield protection and customizable drainage options",
    image: tq07SpecsImage,
    category: ["tech-forward", "wellness-focused"]
  }
];

const categoryInfo = {
  "flagship": {
    title: "Flagship Collection",
    description: "Our premium flagship models representing the pinnacle of luxury and innovation",
    color: "from-gradient-primary to-gradient-secondary"
  },
  "wellness-focused": {
    title: "Wellness Collection", 
    description: "Health and wellness-focused smart toilets with therapeutic features",
    color: "from-green-400 to-emerald-600"
  },
  "tech-forward": {
    title: "Technology Collection",
    description: "Cutting-edge smart toilets with advanced AI and connectivity features",
    color: "from-accent to-primary"
  },
  "luxury": {
    title: "Luxury Collection",
    description: "Premium luxury smart toilets with sophisticated design and materials",
    color: "from-purple-400 to-pink-600"
  },
  "square-design": {
    title: "Square Design Collection",
    description: "Revolutionary square toilet designs with modern aesthetics and space-saving innovation",
    color: "from-amber-500 to-amber-600"
  },
  "egg-design": {
    title: "Egg Design Collection",
    description: "Award-winning egg-shaped smart toilets with iconic spherical design and premium features",
    color: "from-teal-500 to-cyan-500"
  }
};

// Lazy loading component for product cards
const LazyProductCard: React.FC<{ product: ProductReference }> = ({ product }) => {
  const navigate = useNavigate();
  const { getLocalizedPath } = useI18n();
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = cardRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Batch state update in RAF to prevent forced reflow
          requestAnimationFrame(() => {
            setIsVisible(true);
          });
          observer.unobserve(element);
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={cardRef} className="group hover:shadow-xl transition-all duration-300 overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm">
      {isVisible ? (
        <>
          <div className="relative">
            <ImageLightbox
              src={product.images.main}
              alt={`${product.avtName} luxury smart toilet - ${product.tagline}. ${product.description}`}
              title={`${product.avtName} - ${product.tagline}`}
              className="aspect-square"
              linkTo={getLocalizedPath(product.urlSlug)}
              showLinkIcon={true}
              showZoomIcon={false}
            />
            <Badge 
              variant="secondary" 
              className="absolute top-3 right-3 bg-background/90 backdrop-blur-sm"
            >
              {product.priceRange}
            </Badge>
          </div>
          
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{product.avtName}</h3>
              <Badge variant="outline" className="text-xs">
                {product.factoryModel}
              </Badge>
            </div>
            <p className="font-medium text-primary text-sm">{product.tagline}</p>
            <p className="text-sm text-muted-foreground line-clamp-3">{product.description}</p>
            
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Key Features:</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                {product.keyFeatures.slice(0, 3).map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0"></div>
                    {feature}
                  </li>
                ))}
                {product.keyFeatures.length > 3 && (
                  <li className="text-xs text-primary font-medium">
                    +{product.keyFeatures.length - 3} more features
                  </li>
                )}
              </ul>
            </div>
            
            <Button 
              variant="outline" 
              className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
              onClick={() => navigate(getLocalizedPath(product.urlSlug))}
            >
              View Details
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </>
      ) : (
        <div className="aspect-square bg-muted animate-pulse" />
      )}
    </div>
  );
};

// Lazy loading component for feature cards
const LazyFeatureCard: React.FC<{ feature: ProductFeature }> = ({ feature }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = cardRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Batch state update in RAF to prevent forced reflow
          requestAnimationFrame(() => {
            setIsVisible(true);
          });
          observer.unobserve(element);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={cardRef} className="group hover:shadow-lg transition-all duration-300 rounded-lg border bg-card text-card-foreground shadow-sm">
      {isVisible ? (
        <>
          <ImageLightbox
            src={feature.image}
            alt={`${feature.title} - ${feature.description}`}
            title={feature.title}
            className="aspect-[4/3]"
          />
          <div className="p-4">
            <h4 className="font-semibold text-sm mb-2 line-clamp-2">{feature.title}</h4>
            <p className="text-xs text-muted-foreground line-clamp-3">{feature.description}</p>
          </div>
        </>
      ) : (
        <div className="aspect-[4/3] bg-muted animate-pulse" />
      )}
    </div>
  );
};

const ProductCatalog: React.FC<ProductCatalogProps> = ({ selectedCategory }) => {
  // Group products by category
  const productsByCategory = React.useMemo(() => {
    const grouped: Record<string, ProductReference[]> = {};
    
    Object.values(PRODUCT_REFERENCE).forEach(product => {
      if (!grouped[product.category]) {
        grouped[product.category] = [];
      }
      grouped[product.category].push(product);
    });
    
    return grouped;
  }, []);

  // Filter categories based on selection
  const categoriesToShow = selectedCategory 
    ? [selectedCategory] 
    : Object.keys(productsByCategory);

  return (
    <div className="space-y-16">
      {categoriesToShow.map(category => {
        const products = productsByCategory[category];
        const categoryData = categoryInfo[category as keyof typeof categoryInfo];
        
        if (!products || products.length === 0) return null;

        return (
          <section key={category} className="space-y-8">
            {/* Category Header */}
            <div className="text-center space-y-4">
              <div className={`inline-block p-1 rounded-xl bg-gradient-to-r ${categoryData?.color || 'from-gray-400 to-gray-600'}`}>
                <div className="bg-background px-6 py-3 rounded-lg">
                  <h2 className="text-3xl font-bold">{categoryData?.title || `${category} Collection`}</h2>
                </div>
              </div>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {categoryData?.description || `Premium smart toilets in the ${category} category`}
              </p>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
              {products.map(product => (
                <LazyProductCard key={product.avtName} product={product} />
              ))}
            </div>

            {/* Category Features */}
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-center mb-8">Category Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                {productFeatures
                  .filter(feature => feature.category.includes(category))
                  .map(feature => (
                    <LazyFeatureCard key={feature.id} feature={feature} />
                  ))}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default ProductCatalog;